import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { WebhookDeliveryModal } from "@/pages/settings/webhooks/widgets/WebhookDeliveryModal.tsx";
import {
  useWebhook,
  useWebhookDeliveries,
  useWebhookTestDelivery,
} from "@/queries/settings/use-webhooks.query.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  ApiWebhookDelivery,
  ApiWebhookDeliveryFilter,
  ApiWebhookDeliveryStatus,
  ApiWebhookEventType,
  getWebhookEventTypeTitleKey,
} from "@/types/webhook.types.ts";
import {
  Button,
  CoreFilterType,
  EmptyStateBase,
  FilterRenderType,
  FilterValueType,
  Page,
  PaginationBlock,
  Select,
  Stack,
  Table,
  Tag,
  Text,
  useModals,
  useToasts,
} from "@skailer-com/uikit";
import { ColumnDef } from "@tanstack/react-table";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

export function WebhookDeliveriesPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const webhookId = Number(id);
  const { openModal } = useModals();
  const { renderDateAndTime } = useFormatter();
  const { showInfo, showError } = useToasts();
  const [, setSearchParams] = useSearchParams();
  const webhook = useWebhook(webhookId);
  const testDelivery = useWebhookTestDelivery();

  const title = webhook.data?.title || webhook.data?.url || t("core:webhook_delivery_history");
  useTitle(t("core:webhook_delivery_history"), t("core:webhooks"));

  const eventOptions = (webhook.data?.event_types ?? Object.values(ApiWebhookEventType)).map(
    (eventType) => ({
      id: eventType,
      title: t(getWebhookEventTypeTitleKey(eventType)),
    })
  );

  const library = filtersLibrary();
  const {
    filter: { filter },
    filterButton,
  } = useAppFilter<ApiWebhookDeliveryFilter>({
    filters: [
      library.page_number,
      {
        name: "status",
        type: CoreFilterType.hidden,
        valueType: FilterValueType.string,
        renderType: FilterRenderType.custom,
        label: t("common:status"),
        render: (item, { control }) => (
          <Controller
            control={control}
            name={item.name}
            render={({ field }) => (
              <Select
                label={item.label}
                emptyTitle={t("common:all")}
                isClearable
                options={Object.values(ApiWebhookDeliveryStatus).map((status) => ({
                  id: status,
                  title: t(`core:webhook_delivery_status_${status}`),
                }))}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        ),
      },
      {
        name: "event_type",
        type: CoreFilterType.hidden,
        valueType: FilterValueType.string,
        renderType: FilterRenderType.custom,
        label: t("core:webhook_event"),
        render: (item, { control }) => (
          <Controller
            control={control}
            name={item.name}
            render={({ field }) => (
              <Select
                label={item.label}
                emptyTitle={t("common:all")}
                isClearable
                searchable
                options={eventOptions}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        ),
      },
    ],
    source: "Webhook deliveries",
  });
  const deliveries = useWebhookDeliveries({ ...filter, webhook_id: webhookId });

  const columns: ColumnDef<ApiWebhookDelivery>[] = [
    {
      header: t("core:webhook_event"),
      accessorKey: "event_type",
      cell: ({ row }) => <Text>{t(getWebhookEventTypeTitleKey(row.original.event_type))}</Text>,
      ...ColumnSizes.employee,
    },
    {
      header: t("common:status"),
      accessorKey: "status",
      cell: ({ row }) => (
        <Stack direction={"horizontal"}>
          <Tag
            appearance="filled"
            variant={
              row.original.status === ApiWebhookDeliveryStatus.delivered
                ? "positive"
                : row.original.status === ApiWebhookDeliveryStatus.failed
                  ? "danger"
                  : "secondary"
            }
          >
            {t(`core:webhook_delivery_status_${row.original.status}`)}
          </Tag>
        </Stack>
      ),
      ...ColumnSizes.status,
    },
    {
      header: t("common:time"),
      accessorKey: "datetime_created_at",
      cell: ({ row }) => <Text>{renderDateAndTime(row.original.datetime_created_at)}</Text>,
      ...ColumnSizes.historicalDate,
    },
    {
      header: t("core:webhook_http_code"),
      accessorKey: "response_status",
      cell: ({ row }) => <Text>{row.original.response_status ?? "—"}</Text>,
      ...ColumnSizes.status,
    },
    {
      header: t("common:duration"),
      accessorKey: "duration_ms",
      cell: ({ row }) => (
        <Text>{row.original.duration_ms == null ? "—" : `${row.original.duration_ms} ms`}</Text>
      ),
      ...ColumnSizes.status,
    },
    {
      header: t("core:webhook_attempt"),
      accessorKey: "attempt_number",
      cell: ({ row }) => <Text>{row.original.attempt_number}</Text>,
      ...ColumnSizes.status,
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <Stack direction="horizontal" justify="end">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => openModal(WebhookDeliveryModal, { delivery: row.original })}
          >
            {t("common:details")}
          </Button>
        </Stack>
      ),
      ...ColumnSizes.actions,
    },
  ];

  const handleTest = async () => {
    try {
      const result = await testDelivery.mutateAsync(webhookId);
      const description = [
        result.status_code ? `HTTP ${result.status_code}` : null,
        result.duration_ms != null ? `${result.duration_ms} ms` : null,
        result.error_message,
      ]
        .filter(Boolean)
        .join(" · ");
      if (result.success) showInfo({ title: t("core:webhook_test_succeeded"), description });
      else showError({ title: t("core:webhook_test_failed"), description });
    } catch {
      return;
    }
  };

  return (
    <Page className="h-full">
      <Page.Header showBack title={title} navigateTo="/settings/webhooks">
        <Stack direction="horizontal" gap="sm">
          <Button
            variant={"secondary"}
            isLoading={testDelivery.isPending}
            onClick={() => void handleTest()}
          >
            {t("core:webhook_test_delivery")}
          </Button>
          {filterButton({ clearable: true })}
        </Stack>
      </Page.Header>
      <Page.Content stretch>
        <Page.StickyLayout
          footer={
            <PaginationBlock
              metadata={deliveries.data?.metadata}
              onPageChange={(page) =>
                setSearchParams((searchParams) => {
                  searchParams.set("page", page.toString());
                  return searchParams;
                })
              }
            />
          }
        >
          {deliveries.isError || webhook.isError ? (
            <EmptyStateBase
              emoji=":warning:"
              title={t("core:webhook_delivery_load_error")}
              description={t("core:webhook_load_error_description")}
              primaryButton={
                <Button variant="secondary" onClick={() => void deliveries.refetch()}>
                  {t("common:retry")}
                </Button>
              }
            />
          ) : (
            <Table
              stickyHeader={true}
              data={deliveries.data?.items ?? []}
              columns={columns}
              isFetching={deliveries.isFetching}
              showSkeleton={deliveries.isLoading}
              emptyStateComponent={
                <EmptyStateBase
                  variant={"plain"}
                  title={t("core:webhook_delivery_empty_description")}
                />
              }
            />
          )}
        </Page.StickyLayout>
      </Page.Content>
    </Page>
  );
}
